import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {paylogikApi, UserVm} from '../rtkquery/paylogikApi';
import {purgeStoredState} from 'redux-persist';
import AuthProvider, {oidcUtils} from '../../AuthProviders/OIDCProvider';

export interface AuthState {
    authenticated: boolean;
    showLogOut: boolean;
    user: UserVm;
    userLoading: boolean;
    accessToken: string;
    hasError: boolean;
    errorMessage?: string;
    errorAdditionalDetail?: string;
    displayName: string;
    initials: string;
    renewIframe: boolean;
    provider;
    powerBiReportingId: string;
}

const baseSessionState = {
    authenticated: false,
    showLogOut: false,
    user: {},
    hasError: false,
    errorMessage: null,
    errorAdditionalDetail: null,
} as AuthState;

export const loadPaylogikUser = createAsyncThunk(
    'auth/loadPayLogikUser',
    (arg, thunkAPI) => {
        thunkAPI.dispatch(
            paylogikApi.endpoints.getMyUser.initiate(undefined, {
                forceRefetch: true,
            })
        );
    }
);

export const logout = createAsyncThunk('auth/logout', () => {
    setTimeout(() => AuthProvider.logout(), 100);
});

const onAppLoadResult = createAsyncThunk(
    'auth/onAppLoadResult',
    (arg, thunkAPI) => {
        //@ts-ignore
        thunkAPI.dispatch(onLogin(arg));
        thunkAPI.dispatch(loadPaylogikUser());
    }
);

export const onAppLoad = createAsyncThunk('auth/onAppLoad', (arg, thunkAPI) => {
    AuthProvider.authUseEffect(
        (idToken, user, provider) =>
            //@ts-ignore
            thunkAPI.dispatch(onAppLoadResult({idToken, user, provider})),
        (error) => thunkAPI.dispatch(onError(error))
    );
});

const slice = createSlice({
    name: 'auth',
    initialState: baseSessionState,
    reducers: {
        onLogin: (state, action: PayloadAction<{idToken; user; provider}>) => {
            // LogRocket.identify(action.payload.user['cognito:username'], {
            //     name: `${action.payload.user.given_name} ${action.payload.user.family_name}`,
            //     email: action.payload.user.email,
            //     // Add your own custom user variables here, ie:
            // });

            state.authenticated = true;

            state.displayName = `${action.payload.user.given_name} ${action.payload.user.family_name}`;
            state.initials = `${action.payload.user.given_name?.substring(
                0,
                1
            )}${action.payload.user.family_name?.substring(0, 1)}`;
            state.provider = action.payload.provider;
            state.accessToken = action.payload.idToken;
            //If users have issue with RLS in Power BI, this is where to fix it.
            //Power BI uses the User Principal Name (User@domain.com) for employees or the LogikId for non sunset users.
            //Eventually the logikId will be able to be the User Principal Name (UPN) from Entra but for now......
            state.powerBiReportingId = action.payload.user['custom:logikId'].startsWith("hills\\") ? action.payload.user["upn"] : action.payload.user['custom:logikId']
        },
        onError: (state, action: PayloadAction<any>) => {
            // For OIDC, we need to initiate sign in
            AuthProvider.userManager.signinRedirect();
        },
    },
    extraReducers: (builder) => {
        builder.addCase(logout.pending, () => {
            return baseSessionState;
        });
        builder.addMatcher(
            paylogikApi.endpoints.getMyUser.matchPending,
            (state, {payload}) => {
                state.hasError = false;
                state.errorMessage = undefined;
                state.errorAdditionalDetail = undefined;
                state.userLoading = true;
                state.user.permissions = [];
            }
        );
        builder.addMatcher(
            paylogikApi.endpoints.getMyUser.matchRejected,
            (state, {payload}) => {
                switch (payload.status) {
                    case 'FETCH_ERROR':
                        state.errorMessage =
                            'Error connecting to the API to retrieve user information';
                        break;
                    case 401:
                        state.errorMessage = 'Insufficient Permissions';
                        state.errorAdditionalDetail =
                            'Your LOGIK profile has not been granted PayLOGIK Permissions.  Please reach out to your account rep for more information about PayLOGIK or assistance correcting any permissions issues.';
                        break;
                }
                state.hasError = true;
                state.userLoading = false;
            }
        );
        builder.addMatcher(
            paylogikApi.endpoints.getMyUser.matchFulfilled,
            (state, {payload}) => {
                state.hasError = false;
                state.errorMessage = undefined;
                state.errorAdditionalDetail = undefined;
                state.user = payload;
                state.userLoading = false;
                state.hasError = false;
            }
        );
    },
});

export const {onLogin, onError} = slice.actions;

export default slice.reducer;

//export const selectCurrentUser = (state: RootState) => state.auth.user;
